import {EventEmitter, Injectable} from '@angular/core';
import {
  interpolateBlues,
  interpolateBrBG,
  interpolateBuGn,
  interpolateBuPu,
  interpolateCividis,
  interpolateCool,
  interpolateCubehelixDefault,
  interpolateGnBu,
  interpolateGreens,
  interpolateGreys,
  interpolateInferno,
  interpolateMagma,
  interpolateOranges,
  interpolateOrRd,
  interpolatePiYG,
  interpolatePlasma,
  interpolatePRGn,
  interpolatePuBu,
  interpolatePuBuGn,
  interpolatePuOr,
  interpolatePuRd,
  interpolatePurples,
  interpolateRainbow,
  interpolateRdBu,
  interpolateRdGy,
  interpolateRdPu,
  interpolateRdYlBu,
  interpolateRdYlGn,
  interpolateReds,
  interpolateSinebow,
  interpolateSpectral,
  interpolateTurbo,
  interpolateViridis,
  interpolateWarm,
  interpolateYlGn,
  interpolateYlGnBu,
  interpolateYlOrBr,
  interpolateYlOrRd
} from 'd3-scale-chromatic';
import {ColorMapOption} from "./enums/color-map-option";
import {ModelBase} from "../../../../models/model-base";
import {ColorUtils} from "../../../../utils/colors/color-utils";
import {BaseOrderElementDto} from "../models/core/base-order-element-dto";
import {LinkedDtoRepository} from "../repositories/linked-dto-repository";


@Injectable({
  providedIn: 'root'
})
export class ColorMapService {
  counter = 0;

  colorMappingHardResetEmitter = new EventEmitter();

  colorToDtoMapping: { [id: string]: string } = {}
  option: ColorMapOption = ColorMapOption.interpolateMagma//ColorMapOption.interpolateMagma

  colorMappingToFunction: { [option in ColorMapOption]: (n: number) => string } = {

    // Diverging
    [ColorMapOption.interpolateBrBG]: (n: number) => interpolateBrBG(1 - n),
    [ColorMapOption.interpolatePRGn]: (n: number) => interpolatePRGn(1 - n),
    [ColorMapOption.interpolatePiYG]: (n: number) => interpolatePiYG(1 - n),
    [ColorMapOption.interpolatePuOr]: (n: number) => interpolatePuOr(1 - n),
    [ColorMapOption.interpolateRdBu]: (n: number) => interpolateRdBu(1 - n),
    [ColorMapOption.interpolateRdGy]: (n: number) => interpolateRdGy(1 - n),
    [ColorMapOption.interpolateRdYlBu]: (n: number) => interpolateRdYlBu(1 - n),
    [ColorMapOption.interpolateRdYlGn]: (n: number) => interpolateRdYlGn(1 - n),
    [ColorMapOption.interpolateSpectral]: (n: number) => interpolateSpectral(1 - n),

    // Single Hue
    [ColorMapOption.interpolateBlues]: (n: number) => interpolateBlues(1 - n),
    [ColorMapOption.interpolateGreens]: (n: number) => interpolateGreens(1 - n),
    [ColorMapOption.interpolateGreys]: (n: number) => interpolateGreys(1 - n),
    [ColorMapOption.interpolateOranges]: (n: number) => interpolateOranges(1 - n),
    [ColorMapOption.interpolatePurples]: (n: number) => interpolatePurples(1 - n),
    [ColorMapOption.interpolateReds]: (n: number) => interpolateReds(1 - n),

    // Multi Hue
    [ColorMapOption.interpolateTurbo]: (n: number) => interpolateTurbo(n),
    [ColorMapOption.interpolateViridis]: (n: number) => interpolateViridis(n),
    [ColorMapOption.interpolateInferno]: (n: number) => interpolateInferno(n),
    [ColorMapOption.interpolateMagma]: (n: number) => interpolateMagma((n) * 0.9 + 0.1),
    [ColorMapOption.interpolatePlasma]: (n: number) => interpolatePlasma(n),
    [ColorMapOption.interpolateCividis]: (n: number) => interpolateCividis(n),
    [ColorMapOption.interpolateWarm]: (n: number) => interpolateWarm(n),
    [ColorMapOption.interpolateCool]: (n: number) => interpolateCool(n),
    [ColorMapOption.interpolateCubehelixDefault]: (n: number) => interpolateCubehelixDefault(n * 0.9 + 0.1),
    [ColorMapOption.interpolateBuGn]: (n: number) => interpolateBuGn(n),
    [ColorMapOption.interpolateBuPu]: (n: number) => interpolateBuPu(n),
    [ColorMapOption.interpolateGnBu]: (n: number) => interpolateGnBu(n),
    [ColorMapOption.interpolateOrRd]: (n: number) => interpolateOrRd(n),
    [ColorMapOption.interpolatePuBuGn]: (n: number) => interpolatePuBuGn(n),
    [ColorMapOption.interpolatePuBu]: (n: number) => interpolatePuBu(n),
    [ColorMapOption.interpolatePuRd]: (n: number) => interpolatePuRd(n),
    [ColorMapOption.interpolateRdPu]: (n: number) => interpolateRdPu(1 - n),
    [ColorMapOption.interpolateYlGnBu]: (n: number) => interpolateYlGnBu(n),
    [ColorMapOption.interpolateYlGn]: (n: number) => interpolateYlGn(n),
    [ColorMapOption.interpolateYlOrBr]: (n: number) => interpolateYlOrBr(n),
    [ColorMapOption.interpolateYlOrRd]: (n: number) => interpolateYlOrRd(n),

    // Cyclical
    [ColorMapOption.interpolateRainbow]: (n: number) => interpolateRainbow(n),
    [ColorMapOption.interpolateSinebow]: (n: number) => interpolateSinebow(1 - n)

  }

  constructor() {
  }


  // Todo: fix typing, all functions should be static somehow, should also be more generic
  synchronizeDtoDataUtilsWithColorMapping(linkedDtoRepository: LinkedDtoRepository<any>) {
    for (let key in linkedDtoRepository.translationKey) {
      // Todo: remove the BaseOrderElementDto type here
      this.synchronizeColorMappingWithDto(linkedDtoRepository.getValueList(key as keyof BaseOrderElementDto), key)
    }
  }

  synchronizeColorMappingWithDto(dto: ModelBase[], dtoName: string) {
    let dtoLength = dto.length
    let counter = 0
    for (let value of dto) {
      let id = value.id + "_" + dtoName
      this.colorToDtoMapping[id] = this.colorMappingToFunction[this.option](counter / dtoLength)
      counter++
    }
  }

  getColorAsRGB(n: number, count: number, alpha: number | undefined = undefined): string {
    let rgbColor = this.colorMappingToFunction[this.option](n / count)
    return ColorUtils.setAlphaOnRGB(rgbColor, alpha ?? 1)
  }

  getColor(n: number, count: number): string {
    return this.colorMappingToFunction[this.option](n / count)
  }

  getColorAndStore(n: number, count: number, key: string, dtoName: string): string {
    let color = this.colorMappingToFunction[this.option](n / count)
    this.colorToDtoMapping[key + "_" + dtoName] = color
    return color
  }

  getColorFromColorMapping(key: string, dtoName: string): string {
    return this.colorToDtoMapping[key + "_" + dtoName]
  }
}
